/**
 * Read back what the search seam ACTUALLY did for a given block.
 *
 * The backend answers every `@querystring-search` call with a `search_meta`
 * block alongside the rows: the mode it routed to, the status group it applied,
 * the sort it used, the operator, the site scope — and a `*_defaulted` flag for
 * each, saying whether the value came from the caller or from the backend's own
 * default. `facets` (Solr's counts) rides along the same response.
 *
 * That is the whole basis for showing a default instead of silently applying it
 * (item 2): the UI cannot know that "Current" was applied unless the server says
 * so, because the UI deliberately sends nothing in that case.
 *
 * The addon's `reducers/querystringsearch` is what keeps these two fields alive
 * — core's SUCCESS branch rebuilds each subrequest and drops everything except
 * items/total/batching.
 *
 * Key shape: `withQuerystringResults` stores under `content.UID-blockId` when
 * the block is rendered inside a content object, and under plain `blockId`
 * otherwise (the search app's case, since SearchBlockView passes no
 * `properties`). Both are matched so this works on any surface.
 */
import { useSelector, shallowEqual } from 'react-redux';

const EMPTY_FACETS = {};

export default function useSearchMeta(blockId) {
  return useSelector((state) => {
    const subs = state?.querystringsearch?.subrequests || {};
    let sub = blockId ? subs[blockId] : undefined;
    if (!sub && blockId) {
      const suffix = `-${blockId}`;
      const key = Object.keys(subs).find((k) => k.endsWith(suffix));
      sub = key ? subs[key] : undefined;
    }
    return {
      meta: sub?.search_meta || null,
      solrFacets: sub?.facets || EMPTY_FACETS,
      total: typeof sub?.total === 'number' ? sub.total : null,
      loaded: !!sub?.loaded,
    };
  }, shallowEqual);
}
